import { BASELINE_CONSTANTS } from "../../constants/baseline.constants.js";
import { Breakpoint } from "../../hooks/useBreakpoint/useBreakpoint.types.js";
import type { ITheme } from "./Theme.types.js";

export const rem = (value: number): string => `calc(var(${BASELINE_CONSTANTS.REM_BASELINE_PROPERTY}) * ${value})`;

export const defaultTheme = {
    rem,
    colors: {
        primary: {
            "110": "#0032B4",
            "100": "#315AE7",
            "80": "#466BE9",
            "10": "#D6DEFA",
            "5": "#EAEEFD",
        },
        secondary: {
            "110": "#0466FF",
            "100": "#20A1FF",
            "80": "#4DB4FF",
            "10": "#E9F6FF",
            "5": "#F4FAFF",
        },
        tertiary: {
            "110": "#5629E0",
            "100": "#7550E6",
            "80": "#9173EB",
            "10": "#F1EDFC",
            "5": "#F8F6FE",
        },
        success: {
            "110": "#008F68",
            "100": "#00BA88",
            "80": "#1AC194",
            "10": "#CAEFE5",
            "5": "#E6F8F3",
        },
        error: {
            "110": "#D31723",
            "100": "#EA3F4A",
            "80": "#EE656E",
            "10": "#F8D4D7",
            "5": "#FDECED",
        },
        warning: {
            "110": "#E08700",
            "100": "#FFA115",
            "80": "#FFB444",
            "10": "#FFE6C0",
            "5": "#FFF6E8",
        },
        blue: {
            "110": "#0841EF",
            "100": "#2D81F7",
            "80": "#579AF8",
            "10": "#EAF2FE",
            "5": "#F4F9FF",
        },
        turquoise: {
            "110": "#019A9A",
            "100": "#11C6C6",
            "80": "#40D2D2",
            "10": "#E7F9F9",
            "5": "#F3FCFC",
        },
        yellow: {
            "110": "#EAB806",
            "100": "#FAD34A ",
            "80": "#FBDB8A",
            "10": "#FEE8AD ",
            "5": "#FFF7E1",
        },
        orange: {
            "110": "#E68A79",
            "100": "#FF9986",
            "80": "#FFAD9E",
            "10": "#FFE0DB",
            "5": "#FFF5F3",
        },
        salmon: {
            "110": "#E68B8E",
            "100": "#FF9A9E",
            "80": "#FFAEB1",
            "10": "#FFE1E2",
            "5": "#FFF5F5",
        },
        coral: {
            "110": "#F86B83",
            "100": "#FF758C",
            "80": "#FF91A3",
            "10": "#FFD6DD",
            "5": "#FFF1F4",
        },
        pink: {
            "110": "#F68BB2",
            "100": "#FFAFCC",
            "80": "#FFBFD6",
            "10": "#FFE7F0",
            "5": "#FFF7FA",
        },
        brand: {
            linkedin: "#0966C2",
            hubspot: "#ff5c35",
            hubspot_bis: "#FF7A59",
            pipedrive: "#017737",
            salesforce: "#00A1E2",
            zoho: "#F0483E",
            sheet: "#0F9D58",
            other: "#14142B",
        },
    },
    grayscale: {
        "100": "#14142B",
        "90": "#4E4B66",
        "80": "#6E7191",
        "70": "#A0A3BD",
        "50": "#D6D8E7",
        "30": "#E2E4F0",
        "20": "#EFF0F6",
        "10": "#F7F7FC",
        "5": "#FCFCFC",
        white: "#FFFFFF",
        whiteGlass: "rgba(255, 255, 255, 0.6)",
    },
    gradients: {
        darkSpace: "linear-gradient(63deg, #1D1D58 -0.11%, #1D3860 28.92%, #245467 65.46%, #275B69 100%)",
        whiteOverlay:
            "linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, rgba(255, 255, 255, 0.20) 21.5%, rgba(255, 255, 255, 0.90) 85%, #FFF 100%)",
        blueToPurple: "linear-gradient(61deg, #86CDF0 0%, #66A6FF 22.92%, #315AE7 58.33%, #5A56E9 90.63%)",
        purpleToGreen: "linear-gradient(98deg, #6720FF 7.36%, #315AE7 52.12%, #19CBCB 99.77%)",
        newPurpleToGreen: "linear-gradient(98deg, #6720FF 7.36%, #315AE7 52.12%, #1AB7B7 99.77%)",
        purpleToGreen10: "linear-gradient(98deg, #E7E5FF 7.36%, #E0E7FF 58.18%, #DDF7FD 99.77%)",
        purpleToGreenAngled10: "linear-gradient(128deg, #E7E5FF 7.36%, #E0E7FF 58.18%, #DDF7FD 99.77%)",
        linkedin: "linear-gradient(76deg, #345DE8 -2.48%, #77A1F3 100%)",
        lostInSpace: "linear-gradient(168deg, #345AE7 8.74%, #100F11 91.53%)",
        email: "linear-gradient(73deg, #635AC6 2.09%, #BBB6FF 92.96%)",
        darkEmail: "linear-gradient(73deg, #5656ac 2.09%, #8c97d0 92.96%)",
        blueMonday: "linear-gradient(88deg, var(--color-primary-primary-100, #315AE7) 13.22%, var(--color-secondary-secondary-100, #20A1FF) 100%)",
        quatertiary:
            "linear-gradient(111deg, rgba(231, 235, 253, 0.70) 0%, rgba(231, 235, 253, 0.70) 0%, rgba(216, 226, 252, 0.70) 30.73%, rgba(210, 218, 248, 0.70) 70.31%, rgba(218, 224, 252, 0.70) 100%)",
        nocrmio: "linear-gradient(144.54deg, #4696ED 4.04%, #4DC790 94.16%)",
        blackThirstday: "linear-gradient(90deg,rgba(242, 91, 108, 1) 0%, rgba(248, 145, 90, 1) 100%)",
    },
    shadows: {
        button: {
            primary: {
                default: "0px 10px 20px 0px rgba(37, 79, 229, 0.15)",
                hover: "0px 12px 25px 0px rgba(37, 79, 229, 0.20)",
            },
            secondary: {
                default: "0px 10px 20px 0px rgba(32, 161, 255, 0.15)",
                hover: "0px 12px 25px 0px rgba(32, 161, 255, 0.20)",
            },
            tertiary: {
                default: "0px 10px 20px 0px rgba(124, 113, 248, 0.15)",
                hover: "0px 12px 25px 0px rgba(124, 113, 248, 0.20)",
            },
            neutral: {
                default: "0px 8px 20px 0px rgba(78, 75, 102, 0.15)",
                hover: "0px 12px 25px 0px rgba(20, 20, 43, 0.20)",
            },
        },
        card: {
            default: "0px 5px 24px 0px rgba(65, 67, 78, 0.10)",
            hover: "0px 10px 30px 0px rgba(65, 67, 78, 0.15)",
        },
        smallItem: {
            default: "0px 4px 6px 0px rgba(78, 75, 102, 0.15)",
            hover: "0px 6px 8px 0px rgba(78, 75, 102, 0.15)",
        },
        popover: "0px 0px 40px 0px rgba(65, 67, 78, 0.15)",
        linkedin: "rgba(140, 140, 140, 0.2) 0px 0px 0px 1px",
        focus: {
            primary: "0px 0px 0px 3px #EAEEFD",
            secondary: "0px 0px 0px 3px #F3FAFE",
            tertiary: "0px 0px 0px 3px #F8F8FF",
            neutral: "0px 0px 0px 3px #F7F7FC",
        },
    },
    radius: {
        null: "0px",
        xxs: rem(0.25),
        xs: rem(0.5),
        s: rem(0.75),
        m: rem(1),
        l: rem(1.25),
        xl: rem(1.5),
        xxl: rem(2),
        "3xl": rem(2.5),
        round: "50%",
    },
    backgrounds: {
        interface: "#EEF1FC",
        inbox: "rgba(247, 247, 252, 0.98)",
    },
    customScrollbars: true,
    elevationLevels: {
        under: -1,
        ground: 0,
        one: 1,
        two: 10,
        three: 100,
        four: 1000,
        five: 10000,
        six: 100000,
        seven: 1000000,
        eight: 10000000,
        nine: 100000000,
        max: 2147483647,
    },
    breakpoints: {
        [Breakpoint.XS]: 0,
        [Breakpoint.SM]: 576,
        [Breakpoint.MD]: 768,
        [Breakpoint.LG]: 992,
        [Breakpoint.XL]: 1200,
        [Breakpoint.XXL]: 1600,
    },
    spacing: {
        "0": "0",
        "2": rem(0.125),
        "4": rem(0.25),
        "8": rem(0.5),
        "12": rem(0.75),
        "16": rem(1),
        "20": rem(1.25),
        "24": rem(1.5),
        "28": rem(1.75),
        "32": rem(2),
        "36": rem(2.25),
        "40": rem(2.5),
        "44": rem(2.75),
        "48": rem(3),
        "52": rem(3.25),
        "56": rem(3.5),
        "60": rem(3.75),
        "64": rem(4),
        "72": rem(4.5),
        "80": rem(5),
        "96": rem(6),
    },
} as ITheme;
